import { differenceInCalendarDays, differenceInMonths } from 'date-fns';

export const formatProbabilityEventDay = (date: Date) =>
	new Intl.DateTimeFormat('en-US', {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
	})
		.format(date)
		.toUpperCase();

export const formatProbabilityEventDuration = (date: Date | null, nowMs: number) => {
	if (!date) return 'TBD';

	const diffMs = date.getTime() - nowMs;
	if (diffMs <= 0) return '0m';

	const totalMinutes = Math.floor(diffMs / 60000);
	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;

	if (days > 0) {
		return `${days}d ${hours}h ${minutes}m`;
	}

	if (hours > 0) return `${hours}h ${minutes}m`;
	return `${minutes}m`;
};

export const formatProbabilityEventFullDateWithoutTime = (date: Date | null) => {
	if (!date) return 'TBD';

	return new Intl.DateTimeFormat('en-US', {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	}).format(date);
};

export const getRelativeDate = (date: Date | null): string => {
	if (!date) return 'TBD';

	const now = new Date();
	const diffDays = differenceInCalendarDays(now, date);

	if (diffDays < 0) {
		const absDays = Math.abs(diffDays);
		if (absDays === 1) return 'Tomorrow';
		return `In ${absDays} days`;
	}

	if (diffDays === 0) return 'Today';
	if (diffDays === 1) return 'Yesterday';
	if (diffDays < 7) return `${diffDays} days ago`;

	const weeks = Math.floor(diffDays / 7);
	if (weeks === 1) return '1 week ago';
	if (weeks < 5) return `${weeks} weeks ago`;

	const months = differenceInMonths(now, date);
	if (months === 1) return '1 month ago';

	return `${months} months ago`;
};

export interface TimeLeftBreakdown {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}

export const getTimeLeftBreakdown = (
	targetDate: Date | string | null | undefined
): TimeLeftBreakdown => {
	const defaultBreakdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };

	if (!targetDate) return defaultBreakdown;

	const target = new Date(targetDate).getTime();
	const now = new Date().getTime();
	const diffMs = target - now;

	if (diffMs <= 0) return defaultBreakdown;

	const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
	const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
	const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

	return { days, hours, minutes, seconds };
};

export const formatTimeLeftCountdown = (timeLeft: TimeLeftBreakdown): string => {
	const pad = (num: number, length: number) => String(num).padStart(length, '0');
	return `${pad(timeLeft.days, 2)}d : ${pad(timeLeft.hours, 2)}h : ${pad(timeLeft.minutes, 2)}m : ${pad(timeLeft.seconds, 2)}s`;
};

const EASTERN_TIME_ZONE = 'America/New_York';

export const formatUpcomingEventDate = (date: Date | string | null | undefined): string => {
	if (!date) return 'TBD';
	const dateObj = typeof date === 'string' ? new Date(date) : date;

	const weekday = dateObj
		.toLocaleDateString('en-US', {
			weekday: 'long',
			timeZone: EASTERN_TIME_ZONE,
		})
		.toUpperCase();
	const month = dateObj
		.toLocaleDateString('en-US', {
			month: 'long',
			timeZone: EASTERN_TIME_ZONE,
		})
		.toUpperCase();
	const day = dateObj.toLocaleDateString('en-US', {
		day: 'numeric',
		timeZone: EASTERN_TIME_ZONE,
	});
	const time = dateObj
		.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
			timeZone: EASTERN_TIME_ZONE,
		})
		.toUpperCase();

	return `${weekday} • ${month} ${day} • ${time} ET`;
};
